import { useEffect, useState } from 'react';

type ThrottleOptions = {
  wait?: number;
  leading?: boolean;
};

export function useThrottle<T>(value: T, options: ThrottleOptions = {}): T {
  const { wait = 100, leading = false } = options;
  const [state, setState] = useState<T>(value);

  useEffect(() => {
    let timeout: number | null = null;
    let previous: number | null = null;

    const handleTimeout = () => {
      timeout = null;
      if (!leading) {
        setState(value);
      }
    };

    const throttled = () => {
      const now = Date.now();

      if (!previous && !leading) {
        previous = now;
      }

      const remaining = wait - (now - (previous ?? 0));

      if (remaining <= 0 || remaining > wait) {
        if (timeout) {
          clearTimeout(timeout);
          timeout = null;
        }

        previous = now;
        setState(value);
      } else if (!timeout) {
        timeout = window.setTimeout(handleTimeout, remaining);
      }
    };

    throttled();

    return () => {
      if (timeout) {
        clearTimeout(timeout);
        timeout = null;
      }
    };
  }, [value, wait, leading]);

  return state;
}
